// @flow
/** @jsx isolateComponent */

import { Fragment, type Node } from 'react';
import pluralizer from 'pluralize';

import {
  AsyncOverlay,
  AsyncSearch,
  Checkbox,
  FormField,
  GridLayout,
  Icon,
  Modal,
  Text,
} from '@zapier/common-components';
import ShareDetails from '@zapier/team/src/components/ShareDetails';
import ShareMemberList from '@zapier/team/src/components/ShareMemberList';
import ShareMemberMenuItem from '@zapier/team/src/components/ShareMemberMenuItem';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

import ShareToggle from './ShareToggle';

import type {
  OnSearchShareMemberType,
  OnToggleAllType,
  OnToggleShareType,
  SharedItemType,
  SharedWithType,
} from '@zapier/team/src/types';

type Props = {
  /** True to allow user to edit sharing of item */
  canEdit: boolean,
  /** Message to show when there are no share members */
  emptyMemberListMessage?: string,
  /** True if data is still loading */
  isLoading?: boolean,
  /** Item that is being shared */
  item: SharedItemType,
  /** Type of item being shared */
  itemType: string,
  /** Called to close Sharing Editor */
  onClose: () => void,
  /** Called to search for share members */
  onSearch: OnSearchShareMemberType,
  /** Called to toggle sharing with ALL on item */
  onToggleAll: OnToggleAllType,
  /** Called to toggle if item itself is shared */
  onToggleIsShared?: () => void,
  /** Called to toggle if item is shared with entity */
  onToggleShare: OnToggleShareType,
  /** Message to show when a user sets item to shared */
  shareMessage?: Node,
  /** List of entities item is shared with */
  sharedWith: SharedWithType,
  /** Message to show when a user sets item to unshared */
  unshareMessage?: Node,
};

const Styles = labelStyles(__filename, {
  adminMessage: {
    alignItems: 'center',
    display: 'flex',
  },
  memberList: {
    maxHeight: 340,
    minHeight: 200,
    overflowY: 'scroll',
  },
  title: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
  },
});

export const SharingEditor = (props: Props) => {
  const isSharedWithAll = props.sharedWith.some(g => g.id === 'all');

  const handleAdd = share => {
    props.onToggleShare({
      id: share.id,
      folderId: props.item.id,
      shouldShare: true,
      type: share.type,
    });
  };

  const handleRemove = (id, type) => {
    props.onToggleShare({
      id,
      folderId: props.item.id,
      shouldShare: false,
      type,
    });
  };

  return (
    <Modal closeOnOutsideClick={true} onClose={props.onClose} padding={30}>
      <AsyncOverlay isVisible={props.isLoading}>
        <GridLayout gap={16}>
          <ShareDetails
            isSharedWithAll={isSharedWithAll}
            item={props.item}
            shareCount={
              Array.isArray(props.sharedWith) ? props.sharedWith.length : 0
            }
          />
          {props.item.isShared && (
            <Fragment>
              {props.canEdit && (
                <FormField
                  inputFirst={true}
                  isInline={true}
                  label="Share with everyone"
                >
                  <Checkbox
                    checked={isSharedWithAll}
                    onChange={() =>
                      props.onToggleAll({
                        folderId: props.item.id,
                        shouldShare: !isSharedWithAll,
                      })
                    }
                  />
                </FormField>
              )}
              {isSharedWithAll && !props.canEdit && (
                <Text color="batmobile" type="body">
                  Shared with everyone
                </Text>
              )}
              {!isSharedWithAll && props.canEdit && (
                <GridLayout gap={4}>
                  <div css={Styles.title}>
                    <Text type="body-large-strong">Share with others</Text>
                    <div css={Styles.adminMessage}>
                      <Icon color="steel" margin={{ right: 5 }} name="info" />{' '}
                      <Text color="batmobile" type="fine">
                        Admins have access to all shared{' '}
                        {pluralizer(props.itemType, 2)}
                      </Text>
                    </div>
                  </div>
                  <AsyncSearch
                    ariaLabel="Member and team search"
                    autoFocus={true}
                    emptyMessage="No members or team found"
                    isDisabled={isSharedWithAll}
                    isItemDisabled={item =>
                      item.type === 'member' && item.role !== 'member'
                    }
                    MenuItemComponent={ShareMemberMenuItem}
                    onChange={handleAdd}
                    onInputValueChange={props.onSearch}
                    placeholder="Email, name, or team name"
                  />
                </GridLayout>
              )}
              {!isSharedWithAll && (
                <div css={Styles.memberList}>
                  <ShareMemberList
                    canEdit={props.canEdit}
                    emptyMessage={props.emptyMemberListMessage}
                    onRemoveShare={handleRemove}
                    sharedWith={props.sharedWith}
                  />
                </div>
              )}
            </Fragment>
          )}
          {!props.item.isShared && (
            <p>
              <Text type="body">
                This {props.itemType} is private and cannot be seen by others.
              </Text>
            </p>
          )}
          {props.item.isSharable && props.canEdit && props.onToggleIsShared && (
            <ShareToggle
              isShared={props.item.isShared}
              onToggleIsShared={props.onToggleIsShared}
              shareMessage={props.shareMessage}
              unshareMessage={props.unshareMessage}
            />
          )}
        </GridLayout>
      </AsyncOverlay>
    </Modal>
  );
};
